import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { QUIZ, quizKey, type QuizQuestion } from "../../data/games";
import { recordQuizAnswer } from "../../utils/api";
import { AppHeader, Banner, Icon } from "../common/ui";

interface Props {
  token: string;
  onExit: () => void;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
}

const ROUND = 10; // questions per round (randomized from the pool)

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function QuizGame({ token, onExit, theme, onThemeChange }: Props) {
  const { t, i18n } = useTranslation();
  const isRu = !i18n.language?.startsWith("en");
  const pick = (b: { ru: string; en: string }) => (isRu ? b.ru : b.en || b.ru);

  const [seed, setSeed] = useState(0);
  // Options are displayed in shuffled order (perm holds original indexes), so the
  // correct answer isn't always in the same slot; stats still record original indexes.
  const questions: (QuizQuestion & { perm: number[] })[] = useMemo(
    () => shuffle(QUIZ).slice(0, Math.min(ROUND, QUIZ.length))
      .map((q) => ({ ...q, perm: shuffle(q.options.map((_, i) => i)) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seed],
  );

  const [idx, setIdx] = useState(0);
  const [chosen, setChosen] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  const q = questions[idx];

  const choose = (i: number) => {
    if (chosen !== null) return;
    setChosen(i);
    const correct = i === q.correct;
    if (correct) setScore((s) => s + 1);
    recordQuizAnswer(token, quizKey(q), i, correct);  // best-effort stats
  };

  const next = () => {
    if (idx + 1 >= questions.length) { setDone(true); return; }
    setIdx((n) => n + 1);
    setChosen(null);
  };

  const restart = () => {
    setSeed((s) => s + 1);
    setIdx(0); setChosen(null); setScore(0); setDone(false);
  };

  if (done) {
    const pct = Math.round((score / questions.length) * 100);
    const great = pct >= 80;
    return (
      <div className="beeapp__scroll">
        <AppHeader theme={theme} onThemeChange={onThemeChange} onBack={onExit} title={t("games.quiz.title")} />
        <div className="wrap content-cap" style={{ paddingTop: 6, maxWidth: 560 }}>
          <div className="glass glass-lg" style={{ padding: 28, textAlign: "center" }}>
            <div style={{ display: "grid", placeItems: "center", marginBottom: 14 }}>
              <span style={{
                display: "grid", placeItems: "center", width: 64, height: 64, borderRadius: 18,
                background: "linear-gradient(160deg, var(--accent-fill), var(--accent-fill-2))",
                color: "var(--accent-ink)", boxShadow: "0 10px 24px -10px var(--accent-glow)",
              }}>
                <Icon name="trophy" size={30} />
              </span>
            </div>
            <h1 className="t-display" style={{ color: "var(--accent-text)" }}>{score} / {questions.length}</h1>
            <p className="t-h" style={{ marginTop: 4 }}>{great ? t("games.quiz.great") : t("games.quiz.keepGoing")}</p>
            <p className="t-sm muted" style={{ marginTop: 6 }}>{pct}%</p>
            <button className="btn btn-primary btn-block" onClick={restart} style={{ marginTop: 20 }}>
              <Icon name="rotate" size={18} /> {t("games.quiz.again")}
            </button>
            <button className="btn btn-glass btn-block" onClick={onExit} style={{ marginTop: 10 }}>
              {t("games.backToGames")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="beeapp__scroll">
      <AppHeader theme={theme} onThemeChange={onThemeChange} onBack={onExit} title={t("games.quiz.title")} />
      <div className="wrap content-cap" style={{ paddingTop: 6, maxWidth: 560 }}>
        {/* Progress */}
        <div className="between" style={{ marginBottom: 12 }}>
          <span className="eyebrow">{t("games.quiz.question")} {idx + 1} / {questions.length}</span>
          <span className="badge badge-amber"><Icon name="trophy" size={12} /> {score}</span>
        </div>
        <div className="steps" style={{ marginBottom: 18 }}>
          {questions.map((_, i) => (
            <span key={i} className={`steps__dot${i < idx ? " is-done" : i === idx ? " is-active" : ""}`} />
          ))}
        </div>

        <div key={idx} className="anim-fade">
          <h2 className="t-title" style={{ marginBottom: 16, lineHeight: 1.3 }}>{pick(q.q)}</h2>

          <div className="stack" style={{ "--gap": "10px" } as React.CSSProperties}>
            {q.perm.map((i) => {
              const opt = q.options[i];
              const isCorrect = i === q.correct;
              const isChosen = i === chosen;
              let cls = "glass glass-lg";
              let extra: React.CSSProperties = { padding: 15, textAlign: "left", cursor: chosen === null ? "pointer" : "default", border: "1px solid var(--border)", width: "100%", color: "inherit" };
              if (chosen !== null && isCorrect) extra = { ...extra, borderColor: "var(--success)", background: "var(--success-tint, var(--accent-tint))" };
              else if (isChosen && !isCorrect) extra = { ...extra, borderColor: "var(--danger, #e0566f)", opacity: 0.9 };
              return (
                <button key={i} className={cls} style={extra} onClick={() => choose(i)} disabled={chosen !== null}>
                  <div className="row between" style={{ gap: 10 }}>
                    <span className="t-sm" style={{ fontWeight: 700 }}>{pick(opt)}</span>
                    {chosen !== null && isCorrect && <span style={{ color: "var(--success)" }}><Icon name="check" size={18} stroke={2.6} /></span>}
                    {isChosen && !isCorrect && <span style={{ color: "var(--danger, #e0566f)" }}><Icon name="x" size={18} stroke={2.6} /></span>}
                  </div>
                </button>
              );
            })}
          </div>

          {chosen !== null && (
            <div style={{ marginTop: 16 }} className="anim-rise">
              <Banner kind={chosen === q.correct ? "success" : "info"}>{pick(q.explain)}</Banner>
              <button className="btn btn-primary btn-block" onClick={next} style={{ marginTop: 14 }}>
                {idx + 1 >= questions.length ? t("games.quiz.finish") : t("games.quiz.next")}
                <Icon name="chevronRight" size={18} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
